/**
 * One statement as a document: who from, who to, the lines by day and
 * campaign, and the totals. The same element is what prints.
 *
 * Every figure comes from the server in pence; nothing is added up here
 * except a running subtotal per kind for display, which the server's
 * totals (and its reconciliation guard) agree with.
 */
import type { ReactNode } from 'react';
import type { Statement } from '../../api/statements';
import { formatPence, formatRateBp } from '../../api/statements';
import { useVocab } from '../../vocab';
import './statement.css';


function ukDate(d: string): string {
  const t = new Date(`${d}T12:00:00Z`);
  return Number.isNaN(t.getTime()) ? d : t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export function StatementPaper({ s }: { s: Statement }) {
  const v = useVocab();
  const buyer = s.kind === 'buyer';
  const from = s.from;
  const to = s.to;
  const t = s.totals;
  const vatOn = t.vat_rate_bp > 0;
  const heading = buyer ? 'Statement' : `${v.One('supplier')} statement`;
  const kindWords: Record<string, string> = {
    sale: 'Sale',
    credit: 'Credit',
    charge: v.One('lead'),
    chargeback: 'Chargeback',
  };
  // A statement is about money, so it says sold: "Rows sold" (not
  // "Rows accepted").
  const gross = buyer ? `${v.Many('lead')} sold` : `${v.Many('lead')} supplied`;
  const ded = buyer ? 'Credits for returns' : 'Chargebacks for returns';
  const totalWord = buyer ? 'Total due' : 'Total payable to you';

  return (
    <div className="hf-statement-paper" data-testid="statement-paper">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>{heading}</div>
          <div className="muted">{s.period_label}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 600 }}>{s.number ?? 'Draft, not yet issued'}</div>
          {s.number ? (
            <>
              <div className="muted">{`Issued ${ukDate(s.issue_date)}`}</div>
              <div className="muted">{`Due ${ukDate(s.due_date)}`}</div>
            </>
          ) : (
            <div className="muted">{`Due ${from.payment_terms_days} days after issue`}</div>
          )}
          {s.paid_at && <div style={{ fontWeight: 600 }}>{`Paid ${ukDate(s.paid_at)}`}</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, margin: '28px 0' }}>
        <Block title="From">
          <div style={{ fontWeight: 600 }}>{from.legal_name || 'Your organisation'}</div>
          {from.address && <div className="pre">{from.address}</div>}
          {from.company_number && <div className="muted">{`Company no. ${from.company_number}`}</div>}
          {from.vat_number && <div className="muted">{`VAT no. ${from.vat_number}`}</div>}
        </Block>
        <Block title={buyer ? 'To' : 'Payee'}>
          <div style={{ fontWeight: 600 }}>{to.name || s.party_name}</div>
          {to.address && <div className="pre">{to.address}</div>}
          {to.email && <div className="muted">{to.email}</div>}
          {to.reference && <div className="muted">{`Your reference ${to.reference}`}</div>}
          {!buyer && to.vat_number && <div className="muted">{`VAT no. ${to.vat_number}`}</div>}
        </Block>
        <Block title="Period">
          <div>{`${ukDate(s.period_start)} to ${ukDate(s.period_end)}`}</div>
          <div className="muted">UK time</div>
        </Block>
      </div>

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>{v.One('campaign')}</th>
            <th />
            <th className="num">{v.Many('lead')}</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {s.lines.length === 0 && (
            <tr><td colSpan={5} className="muted">Nothing this month.</td></tr>
          )}
          {s.lines.map((l, i) => {
            const minus = l.kind === 'credit' || l.kind === 'chargeback';
            return (
              <tr key={`${l.day}-${l.kind}-${l.campaign_id}-${i}`}>
                <td style={{ whiteSpace: 'nowrap' }}>{ukDate(l.day)}</td>
                <td>{l.campaign_name || l.campaign_id}</td>
                <td className="muted">{kindWords[l.kind] ?? l.kind}</td>
                <td className="num">{l.count}</td>
                <td className="num">{formatPence(minus ? -l.amount_pence : l.amount_pence)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <table className="totals" style={{ marginTop: 18, maxWidth: 380, marginLeft: 'auto' }}>
        <tbody>
          <tr><td>{`${gross} (${t.gross_count})`}</td><td className="num">{formatPence(t.gross_pence)}</td></tr>
          <tr><td>{`${ded} (${t.deductions_count})`}</td><td className="num">{formatPence(-t.deductions_pence)}</td></tr>
          <tr><td>Net</td><td className="num">{formatPence(t.net_pence)}</td></tr>
          <tr>
            <td>{vatOn ? `VAT at ${formatRateBp(t.vat_rate_bp)}` : 'VAT'}</td>
            <td className="num">{vatOn ? formatPence(t.vat_pence) : 'Not charged'}</td>
          </tr>
          <tr className="grand"><td>{t.total_pence < 0 ? 'Total credit' : totalWord}</td><td className="num">{formatPence(t.total_pence)}</td></tr>
        </tbody>
      </table>

      <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
        {buyer && from.bank_details && (
          <Block title="How to pay">
            <div className="pre">{from.bank_details}</div>
            <div className="muted">{`Please quote ${s.number ?? 'the statement number'}.`}</div>
          </Block>
        )}
        {!buyer && to.bank_details && (
          <Block title="Paid to">
            <div className="pre">{to.bank_details}</div>
          </Block>
        )}
        <Block title="Notes">
          <div className="muted">
            {buyer
              ? `Sales are listed on the day they were made and credits on the day the return was approved, so a return of an earlier month's ${v.one('lead')} is credited here. The per-${v.one('lead')} CSV lists every line.`
              : `${v.Many('lead')} are listed on the day they arrived and chargebacks on the day the return was approved. The per-${v.one('lead')} CSV lists every line.`}
          </div>
        </Block>
      </div>
    </div>
  );
}
